import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_ORIGIN = process.env.REACT_APP_API_URL || 'https://trading.omniscalping.com';

const emptySchedule = () => ({
  id: `sch-${Date.now()}`,
  enabled: true,
  label: 'Scheduled run',
  frequency: 'weekly',
  time: '03:00',
  dayOfWeek: 5,
  timezone: 'America/Chicago',
});

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
  };
}

function toggleIn(list, value, on) {
  const set = new Set(list || []);
  if (on) set.add(value);
  else set.delete(value);
  return [...set];
}

function CheckList({ title, items, selected, onToggle, empty }) {
  return (
    <div className="backup-field">
      <div className="backup-field-label">{title}</div>
      <div className="backup-checklist">
        {!items.length && <div className="backup-empty">{empty || 'Nothing found on this server.'}</div>}
        {items.map((item) => (
          <label key={item} className="backup-check">
            <input
              type="checkbox"
              checked={(selected || []).includes(item)}
              onChange={(e) => onToggle(item, e.target.checked)}
            />
            <span>{item}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function AdminBackups() {
  const [config, setConfig] = useState(null);
  const [catalog, setCatalog] = useState({ sqliteFiles: [], mysqlDatabases: [], folders: [], excludePresets: [] });
  const [runs, setRuns] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folderFilter, setFolderFilter] = useState('');

  const load = useCallback(async () => {
    const [c, r, d] = await Promise.all([
      fetch(`${API_ORIGIN}/api/admin/backups/config`, { headers: authHeaders() }),
      fetch(`${API_ORIGIN}/api/admin/backups/runs`, { headers: authHeaders() }),
      fetch(`${API_ORIGIN}/api/admin/backups/discover`, { headers: authHeaders() }),
    ]);
    if (!c.ok) throw new Error('Failed to load backup config (admin only)');
    const cj = await c.json();
    const rj = r.ok ? await r.json() : { runs: [] };
    const dj = d.ok ? await d.json() : { catalog: {} };
    setConfig(cj.config);
    setRuns(rj.runs || []);
    setCatalog({
      sqliteFiles: dj.catalog?.sqliteFiles || [],
      mysqlDatabases: dj.catalog?.mysqlDatabases || [],
      folders: dj.catalog?.folders || [],
      excludePresets: dj.catalog?.excludePresets || [],
    });
    const ids = Object.keys(cj.config?.jobs || {});
    setJobId((prev) => (prev && cj.config.jobs[prev] ? prev : ids[0] || null));
  }, []);

  useEffect(() => {
    load().catch((e) => setMsg({ err: true, text: e.message }));
  }, [load]);

  const job = jobId && config?.jobs?.[jobId];

  const folderOptions = useMemo(() => {
    const q = folderFilter.trim().toLowerCase();
    const extra = [...(job?.sourcePaths || []), ...(job?.extraPaths || [])];
    const all = [...new Set([...(catalog.folders || []), ...extra])].sort();
    if (!q) return all;
    return all.filter((p) => p.toLowerCase().includes(q));
  }, [catalog.folders, folderFilter, job]);

  const sqliteOptions = useMemo(
    () => [...new Set([...(catalog.sqliteFiles || []), ...(job?.sqliteFiles || [])])],
    [catalog.sqliteFiles, job]
  );
  const mysqlOptions = useMemo(
    () => [...new Set([...(catalog.mysqlDatabases || []), ...(job?.mysqlDatabases || [])])],
    [catalog.mysqlDatabases, job]
  );
  const excludeOptions = useMemo(
    () => [...new Set([...(catalog.excludePresets || []), ...(job?.excludeGlobs || [])])],
    [catalog.excludePresets, job]
  );

  const patchJob = (fields) => {
    setConfig((prev) => ({
      ...prev,
      jobs: { ...prev.jobs, [jobId]: { ...prev.jobs[jobId], ...fields } },
    }));
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/backups/config`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ jobs: config.jobs }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setConfig(data.config);
      setMsg({ err: false, text: 'Saved. Schedules reloaded on the server.' });
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const createProfile = async () => {
    setMsg(null);
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/backups/profiles`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: 'New backup profile' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not create profile');
      setConfig(data.config);
      setJobId(data.jobId);
      setMsg({ err: false, text: 'New profile created. Select items, destination, and schedule, then Save.' });
    } catch (e) {
      setMsg({ err: true, text: e.message });
    }
  };

  const removeProfile = async () => {
    if (!window.confirm(`Delete profile “${job.name}”?`)) return;
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/backups/profiles/${encodeURIComponent(jobId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not delete');
      setConfig(data.config);
      setJobId(Object.keys(data.config.jobs)[0]);
    } catch (e) {
      setMsg({ err: true, text: e.message });
    }
  };

  const runNow = async () => {
    if (!window.confirm(`Run “${job.name}” now?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/backups/run/${encodeURIComponent(jobId)}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Backup failed');
      setMsg({ err: false, text: data.run?.message || 'Backup finished' });
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!config || !job) {
    return (
      <div className="backup-panel">
        {msg ? <p className="backup-banner backup-banner-err">{msg.text}</p> : <p>Loading backups…</p>}
      </div>
    );
  }

  const dest = job.destination || {};
  const selectedFolders = [...new Set([...(job.sourcePaths || []), ...(job.extraPaths || [])])];

  return (
    <div className="backup-panel">
      <h2>Backups</h2>
      <p className="backup-lead">
        Create as many profiles as you need. Each profile has its own folders, databases, exclusions, destination, and
        schedule. Check what to include — typing is not required.
      </p>

      {msg && <div className={`backup-banner ${msg.err ? 'backup-banner-err' : 'backup-banner-ok'}`}>{msg.text}</div>}

      <div className="backup-toolbar">
        {Object.values(config.jobs).map((j) => (
          <button key={j.id} type="button" className={jobId === j.id ? 'active' : ''} onClick={() => setJobId(j.id)}>
            {j.name}
          </button>
        ))}
        <button type="button" className="backup-add-profile" onClick={createProfile}>
          + New profile
        </button>
      </div>

      <section className="backup-card">
        <h3>Profile</h3>
        <div className="backup-field">
          <div className="backup-field-label">Name</div>
          <input className="backup-input" value={job.name || ''} onChange={(e) => patchJob({ name: e.target.value })} />
        </div>
        <label className="backup-check">
          <input type="checkbox" checked={!!job.enabled} onChange={(e) => patchJob({ enabled: e.target.checked })} />
          <span>Profile enabled (schedules only run when this is on)</span>
        </label>
        <div className="backup-field">
          <div className="backup-field-label">Keep this many completed copies</div>
          <select
            className="backup-input backup-input-sm"
            value={job.retentionCount || 14}
            onChange={(e) => patchJob({ retentionCount: parseInt(e.target.value, 10) })}
          >
            {[3, 5, 7, 8, 14, 21, 30, 60].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="backup-btn-danger" onClick={removeProfile}>
          Delete this profile
        </button>
      </section>

      <section className="backup-card">
        <h3>SQLite databases</h3>
        <p className="backup-hint">Hot copy of selected .db files (all tables).</p>
        <CheckList
          title="Select databases"
          items={sqliteOptions}
          selected={job.sqliteFiles || []}
          onToggle={(item, on) => patchJob({ sqliteFiles: toggleIn(job.sqliteFiles, item, on), includeSqlite: true })}
        />
      </section>

      <section className="backup-card">
        <h3>MySQL databases</h3>
        <label className="backup-check">
          <input
            type="checkbox"
            checked={!!job.includeMysql}
            onChange={(e) => patchJob({ includeMysql: e.target.checked })}
          />
          <span>Include MySQL dump</span>
        </label>
        <label className="backup-check">
          <input
            type="checkbox"
            checked={!!job.mysqlDumpAll}
            onChange={(e) => patchJob({ mysqlDumpAll: e.target.checked })}
          />
          <span>Dump every MySQL database (overrides the list)</span>
        </label>
        {!job.mysqlDumpAll && (
          <CheckList
            title="Select MySQL databases"
            items={mysqlOptions}
            selected={job.mysqlDatabases || []}
            onToggle={(item, on) => patchJob({ mysqlDatabases: toggleIn(job.mysqlDatabases, item, on) })}
          />
        )}
      </section>

      <section className="backup-card">
        <h3>Folders</h3>
        <p className="backup-hint">Check every folder this profile should copy. Filter the list if it is long.</p>
        <div className="backup-field">
          <div className="backup-field-label">Filter folder list</div>
          <input
            className="backup-input"
            value={folderFilter}
            onChange={(e) => setFolderFilter(e.target.value)}
            placeholder="Optional filter…"
          />
        </div>
        <CheckList
          title="Select folders"
          items={folderOptions}
          selected={selectedFolders}
          onToggle={(item, on) => {
            const next = toggleIn(selectedFolders, item, on);
            patchJob({ sourcePaths: next, extraPaths: [] });
          }}
          empty="No matching folders on this server."
        />
      </section>

      <section className="backup-card">
        <h3>Exclusions</h3>
        <p className="backup-hint">Checked names are skipped inside selected folders (node_modules, .git, logs, …).</p>
        <CheckList
          title="Skip these"
          items={excludeOptions}
          selected={job.excludeGlobs || []}
          onToggle={(item, on) => patchJob({ excludeGlobs: toggleIn(job.excludeGlobs, item, on) })}
        />
      </section>

      <section className="backup-card">
        <h3>Destination</h3>
        <div className="backup-field">
          <div className="backup-field-label">Where to store copies</div>
          <select
            className="backup-input"
            value={dest.type || 'local'}
            onChange={(e) => patchJob({ destination: { ...dest, type: e.target.value } })}
          >
            <option value="local">Mounted NAS / local path</option>
            <option value="sftp">SFTP (SSH to NAS)</option>
          </select>
        </div>
        {(dest.type || 'local') === 'local' && (
          <div className="backup-field">
            <div className="backup-field-label">Path on this VPS</div>
            <input
              className="backup-input"
              value={dest.localPath || ''}
              onChange={(e) => patchJob({ destination: { ...dest, localPath: e.target.value } })}
            />
          </div>
        )}
        {dest.type === 'sftp' && (
          <>
            <div className="backup-field">
              <div className="backup-field-label">NAS host</div>
              <input
                className="backup-input"
                value={dest.host || ''}
                onChange={(e) => patchJob({ destination: { ...dest, host: e.target.value } })}
              />
            </div>
            <div className="backup-field">
              <div className="backup-field-label">Port</div>
              <select
                className="backup-input backup-input-sm"
                value={dest.port || 22}
                onChange={(e) => patchJob({ destination: { ...dest, port: parseInt(e.target.value, 10) || 22 } })}
              >
                <option value={22}>22</option>
                <option value={2222}>2222</option>
              </select>
            </div>
            <div className="backup-field">
              <div className="backup-field-label">SSH username</div>
              <input
                className="backup-input"
                value={dest.username || ''}
                onChange={(e) => patchJob({ destination: { ...dest, username: e.target.value } })}
              />
            </div>
            <div className="backup-field">
              <div className="backup-field-label">Folder on the NAS</div>
              <input
                className="backup-input"
                value={dest.remotePath || ''}
                onChange={(e) => patchJob({ destination: { ...dest, remotePath: e.target.value } })}
              />
            </div>
            <p className="backup-hint">Uses this VPS SSH key (no password prompt).</p>
          </>
        )}
      </section>

      <section className="backup-card">
        <h3>Schedule for this profile</h3>
        {(job.schedules || []).map((sch, idx) => (
          <div key={sch.id || idx} className="backup-schedule">
            <label className="backup-check">
              <input
                type="checkbox"
                checked={!!sch.enabled}
                onChange={(e) => {
                  const schedules = [...job.schedules];
                  schedules[idx] = { ...sch, enabled: e.target.checked };
                  patchJob({ schedules });
                }}
              />
              <span>On</span>
            </label>
            <div className="backup-field">
              <div className="backup-field-label">How often</div>
              <select
                className="backup-input"
                value={sch.frequency || 'daily'}
                onChange={(e) => {
                  const schedules = [...job.schedules];
                  schedules[idx] = { ...sch, frequency: e.target.value };
                  patchJob({ schedules });
                }}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="backup-field">
              <div className="backup-field-label">Time</div>
              <input
                className="backup-input"
                type="time"
                value={sch.time || '02:00'}
                onChange={(e) => {
                  const schedules = [...job.schedules];
                  schedules[idx] = { ...sch, time: e.target.value };
                  patchJob({ schedules });
                }}
              />
            </div>
            {sch.frequency === 'weekly' && (
              <div className="backup-field">
                <div className="backup-field-label">Day</div>
                <select
                  className="backup-input"
                  value={sch.dayOfWeek ?? 5}
                  onChange={(e) => {
                    const schedules = [...job.schedules];
                    schedules[idx] = { ...sch, dayOfWeek: parseInt(e.target.value, 10) };
                    patchJob({ schedules });
                  }}
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}
            <button
              type="button"
              className="backup-btn-danger"
              onClick={() => patchJob({ schedules: job.schedules.filter((_, i) => i !== idx) })}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="backup-btn-secondary"
          onClick={() => patchJob({ schedules: [...(job.schedules || []), emptySchedule()] })}
        >
          Add schedule
        </button>
      </section>

      <div className="backup-actions">
        <button type="button" className="backup-btn-primary" onClick={save} disabled={saving || busy}>
          {saving ? 'Saving…' : 'Save this profile'}
        </button>
        <button type="button" className="backup-btn-primary" onClick={runNow} disabled={busy || saving}>
          {busy ? 'Running…' : 'Run this profile now'}
        </button>
      </div>

      <section className="backup-card">
        <h3>Recent runs</h3>
        <table className="backup-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Profile</th>
              <th>Status</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {!runs.length && (
              <tr>
                <td colSpan={4}>No runs yet.</td>
              </tr>
            )}
            {runs.slice(0, 30).map((r) => (
              <tr key={r.id}>
                <td>{r.startedAt?.replace('T', ' ').slice(0, 19)}</td>
                <td>{r.jobName || r.jobId}</td>
                <td>{r.status}</td>
                <td>{r.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
